import { useMemo } from 'react';
import { IScoreboardTheme, DEFAULT_THEME } from '../types';

/**
 * CSS variable names used by the scoreboard theme system.
 * These map directly to Chakra semantic tokens when migrating.
 */
export const SCOREBOARD_CSS_VARS = {
  primaryColor: '--scoreboard-primary',
  secondaryColor: '--scoreboard-secondary',
  backgroundColor: '--scoreboard-background',
  bannerBackground: '--scoreboard-banner-bg',
  cardBackground: '--scoreboard-card-bg',
  textPrimary: '--scoreboard-text-primary',
  textSecondary: '--scoreboard-text-secondary',
  fontFamily: '--scoreboard-font-family',
} as const;

/**
 * Title font size presets in pixels
 */
const TITLE_FONT_SIZES = {
  small: '24px',
  medium: '32px',
  large: '48px',
  xlarge: '64px',
} as const;

export interface IScoreboardCSSVariables {
  [SCOREBOARD_CSS_VARS.primaryColor]: string;
  [SCOREBOARD_CSS_VARS.secondaryColor]: string;
  [SCOREBOARD_CSS_VARS.backgroundColor]: string;
  [SCOREBOARD_CSS_VARS.bannerBackground]: string;
  [SCOREBOARD_CSS_VARS.cardBackground]: string;
  [SCOREBOARD_CSS_VARS.textPrimary]: string;
  [SCOREBOARD_CSS_VARS.textSecondary]: string;
  [SCOREBOARD_CSS_VARS.fontFamily]: string;
  '--scoreboard-title-size': string;
}

export interface IBackgroundStyle {
  background: string;
  backgroundSize?: string;
  backgroundPosition?: string;
  backgroundRepeat?: string;
}

export interface UseScoreboardThemeResult {
  /** CSS variables object to spread on a container element */
  cssVariables: IScoreboardCSSVariables;
  /** Inline style object for the container */
  containerStyle: React.CSSProperties;
  /** Background-specific styles */
  backgroundStyle: IBackgroundStyle;
  /** The resolved theme with defaults applied */
  resolvedTheme: IScoreboardTheme;
}

/**
 * Hook that converts theme configuration into CSS variables and styles.
 *
 * Works with styled-components now and will trivially adapt to Chakra's
 * useToken/semantic tokens system later.
 *
 * @param theme - The scoreboard theme configuration (or partial)
 * @returns CSS variables and styles to apply to the scoreboard container
 *
 * @example
 * ```tsx
 * const { cssVariables, containerStyle } = useScoreboardTheme(settings.theme);
 * return <div style={{ ...containerStyle, ...cssVariables }}>...</div>;
 * ```
 */
export const useScoreboardTheme = (
  theme: Partial<IScoreboardTheme> | undefined
): UseScoreboardThemeResult => {
  return useMemo(() => {
    // Merge with defaults
    const resolvedTheme: IScoreboardTheme = {
      ...DEFAULT_THEME,
      ...theme,
    };

    // Build CSS variables
    const cssVariables: IScoreboardCSSVariables = {
      [SCOREBOARD_CSS_VARS.primaryColor]: resolvedTheme.primaryColor,
      [SCOREBOARD_CSS_VARS.secondaryColor]: resolvedTheme.secondaryColor,
      [SCOREBOARD_CSS_VARS.backgroundColor]: resolvedTheme.backgroundColor,
      [SCOREBOARD_CSS_VARS.bannerBackground]: resolvedTheme.bannerBackground,
      [SCOREBOARD_CSS_VARS.cardBackground]: resolvedTheme.cardBackground,
      [SCOREBOARD_CSS_VARS.textPrimary]: resolvedTheme.textPrimary,
      [SCOREBOARD_CSS_VARS.textSecondary]: resolvedTheme.textSecondary,
      [SCOREBOARD_CSS_VARS.fontFamily]: resolvedTheme.fontFamily || 'inherit',
      '--scoreboard-title-size':
        TITLE_FONT_SIZES[resolvedTheme.titleFontSize || 'large'],
    };

    // Build background style based on type
    const backgroundStyle = buildBackgroundStyle(resolvedTheme);

    // Combined container style
    const containerStyle: React.CSSProperties = {
      ...backgroundStyle,
      color: resolvedTheme.textPrimary,
      fontFamily: resolvedTheme.fontFamily || 'inherit',
    };

    return {
      cssVariables,
      containerStyle,
      backgroundStyle,
      resolvedTheme,
    };
  }, [theme]);
};

/**
 * Builds the background CSS based on theme configuration
 */
function buildBackgroundStyle(theme: IScoreboardTheme): IBackgroundStyle {
  switch (theme.backgroundType) {
    case 'gradient':
      return {
        background: `linear-gradient(135deg, ${theme.backgroundColor} 0%, ${theme.backgroundColorSecondary} 100%)`,
      };

    case 'image':
      if (theme.backgroundImage) {
        const { backgroundImage } = theme;
        const imageUrl = getBackgroundImageUrl(backgroundImage);
        const opacity = backgroundImage.opacity ?? 0.3;

        // Use a pseudo-element approach via CSS or overlay
        // For inline styles, we layer the image with a color overlay
        return {
          background: `
            linear-gradient(
              rgba(0, 0, 0, ${1 - opacity}),
              rgba(0, 0, 0, ${1 - opacity})
            ),
            url(${imageUrl})
          `.trim(),
          backgroundSize: backgroundImage.size || 'auto 100%',
          backgroundPosition: backgroundImage.position || 'center',
          backgroundRepeat: 'no-repeat',
        };
      }
      // Fallback to solid if no image configured
      return { background: theme.backgroundColor };

    case 'solid':
    default:
      return { background: theme.backgroundColor };
  }
}

/**
 * Resolves background image URL from preset or custom configuration
 */
function getBackgroundImageUrl(
  backgroundImage: NonNullable<IScoreboardTheme['backgroundImage']>
): string {
  if (backgroundImage.type === 'custom' && backgroundImage.customUrl) {
    const url = backgroundImage.customUrl;
    // Only allow http(s) and data URIs to prevent injection via url() in CSS
    if (/^(https?:\/\/|data:image\/)/.test(url)) {
      return url;
    }
    return '';
  }

  // Built-in presets - these would be served from /assets/scoreboard/backgrounds/
  const PRESET_URLS: Record<string, string> = {
    'stadium-lights': '/assets/scoreboard/backgrounds/stadium-lights.jpg',
    'turf-texture': '/assets/scoreboard/backgrounds/turf-texture.jpg',
    'concrete-dark': '/assets/scoreboard/backgrounds/concrete-dark.jpg',
    'gradient-blue': '/assets/scoreboard/backgrounds/gradient-blue.jpg',
    'gradient-orange': '/assets/scoreboard/backgrounds/gradient-orange.jpg',
    'abstract-lines': '/assets/scoreboard/backgrounds/abstract-lines.jpg',
  };

  const presetId = backgroundImage.presetId;
  if (presetId && PRESET_URLS[presetId]) {
    return PRESET_URLS[presetId];
  }

  // Fallback - shouldn't happen if properly configured
  return '';
}

/**
 * Helper to apply scoreboard CSS variables to an element via ref
 * Useful for imperative updates or animation libraries
 */
export const applyThemeToElement = (
  element: HTMLElement,
  theme: IScoreboardTheme
): void => {
  element.style.setProperty(
    SCOREBOARD_CSS_VARS.primaryColor,
    theme.primaryColor
  );
  element.style.setProperty(
    SCOREBOARD_CSS_VARS.secondaryColor,
    theme.secondaryColor
  );
  element.style.setProperty(
    SCOREBOARD_CSS_VARS.backgroundColor,
    theme.backgroundColor
  );
  element.style.setProperty(
    SCOREBOARD_CSS_VARS.bannerBackground,
    theme.bannerBackground
  );
  element.style.setProperty(SCOREBOARD_CSS_VARS.textPrimary, theme.textPrimary);
  element.style.setProperty(
    SCOREBOARD_CSS_VARS.textSecondary,
    theme.textSecondary
  );
  if (theme.fontFamily) {
    element.style.setProperty(SCOREBOARD_CSS_VARS.fontFamily, theme.fontFamily);
  }
};
